import React, { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { clearAuthSession } from "../services/authService";

const CHAT_UNREAD_STORAGE_KEY = "chatUnreadCount";

const getSavedChatUnreadCount = () => {
  const value = Number(localStorage.getItem(CHAT_UNREAD_STORAGE_KEY) || 0);

  return Number.isNaN(value) ? 0 : value;
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [chatUnreadCount, setChatUnreadCount] = useState(getSavedChatUnreadCount);
  const [accountOpen, setAccountOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(
    () => location.pathname.startsWith("/settings") || ["/notifications", "/all-notifications"].includes(location.pathname),
  );
  const [admin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("authUser") || "{}"); }
    catch { return {}; }
  });
  const adminName = admin.name || admin.fullName || admin.email || "Admin";
  const initials = adminName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "A";
  const handleLogout = () => { clearAuthSession(); navigate("/login"); };

  useEffect(() => {
    const handleChatNotification = (event) => {
      if (location.pathname === "/chat") return;
      if (!event.detail?.normalizedMessage?.message?.trim()) return;

      setChatUnreadCount((previousCount) => previousCount + 1);
    };

    window.addEventListener("chat-notification", handleChatNotification);

    return () => {
      window.removeEventListener("chat-notification", handleChatNotification);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/chat") {
      setChatUnreadCount(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/settings") || ["/notifications", "/all-notifications"].includes(location.pathname)) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(CHAT_UNREAD_STORAGE_KEY, chatUnreadCount.toString());
  }, [chatUnreadCount]);

  return (
    <div className="main-wrapper">
      <div className="sidebar">
        <div className="sidebar-header">
            <NavLink to="/dashboard" className="sidebar-brand">
                <img src="/assets/img/kare-logo.png" alt="Kare" />
            </NavLink>
            <NavLink className="sidebar-toggler-mobile">
                <img src="/assets/img/kare-mark.svg" alt="Kare" />
            </NavLink>
        </div>
        <div className="sidebar-body">
            <div className="main-navigation">
                <ul>
                    <li className="nav-item">
                        <NavLink to="/dashboard" className="nav-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-layout-dashboard-icon lucide-layout-dashboard"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
                            <span className="hide-menu">Dashboard</span>
                        </NavLink>
                    </li>

                    <li className="nav-item">
                        <NavLink to="/archives" className="nav-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-package-icon lucide-package"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><polyline points="3.29 7 12 12 20.71 7"/><path d="m7.5 4.27 9 5.15"/></svg>
                            <span className="hide-menu">Archives</span>
                        </NavLink>
                    </li>

                    <li className="nav-item">
                        <NavLink to="/products" className="nav-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-box" viewBox="0 0 16 16">
                                <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z"/>
                            </svg>
                            <span className="hide-menu">Medications</span>
                        </NavLink>
                    </li>

                    <li className="nav-item">
                        <NavLink to="/otc" className="nav-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m10.5 20.5 10-10a3.54 3.54 0 0 0-5-5l-10 10a3.54 3.54 0 0 0 5 5Z"/><path d="m8.5 8.5 7 7"/></svg>
                            <span className="hide-menu">OTC</span>
                        </NavLink>
                    </li>

                    <li className="nav-item">
                        <NavLink to="/users" className="nav-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-users-icon lucide-users"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><path d="M16 3.128a4 4 0 0 1 0 7.744"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><circle cx="9" cy="7" r="4"/></svg>
                            <span className="hide-menu">Users</span>
                        </NavLink>
                    </li>

	                    <li className="nav-item">
	                        <NavLink to="/chat" className="nav-link">
	                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle-more-icon lucide-message-circle-more"><path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"/><path d="M8 12h.01"/><path d="M12 12h.01"/><path d="M16 12h.01"/></svg>
	                            <span className="hide-menu">Chat</span>
	                            {chatUnreadCount > 0 && (
	                              <span className="sidebar-chat-badge">
	                                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
	                              </span>
	                            )}
	                        </NavLink>
	                    </li>

                    <li className="nav-item">
                        <NavLink to="/billing" className="nav-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="16" height="18" x="4" y="3" rx="2"/><path d="M8 7h8M8 11h8M8 15h3"/></svg>
                            <span className="hide-menu">Billing</span>
                        </NavLink>
                    </li>
                    <li className={`nav-item sidebar-settings-item ${settingsOpen ? "is-open" : ""} ${location.pathname.startsWith("/settings") || ["/notifications", "/all-notifications"].includes(location.pathname) ? "is-active" : ""}`}>
                        <button type="button" className="nav-link sidebar-submenu-trigger" onClick={() => setSettingsOpen((open) => !open)} aria-expanded={settingsOpen}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings-icon lucide-settings"><path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"/><circle cx="12" cy="12" r="3"/></svg>
                            <span className="hide-menu">Settings</span>
                            <i className={`bi bi-chevron-${settingsOpen ? "up" : "down"} sidebar-submenu-chevron`}></i>
                        </button>
                        {settingsOpen && <ul className="sidebar-submenu">
                          <li><NavLink to="/settings/categories" className="nav-link"><i className="bi bi-tags"></i><span className="hide-menu">Categories</span></NavLink></li>
                          <li><NavLink to="/settings/kupat-holims" className="nav-link"><i className="bi bi-hospital"></i><span className="hide-menu">Kupat Holim</span></NavLink></li>
                          <li><NavLink to="/notifications" className="nav-link"><i className="bi bi-send"></i><span className="hide-menu">Send Notification</span></NavLink></li>
                          <li><NavLink to="/all-notifications" className="nav-link"><i className="bi bi-bell"></i><span className="hide-menu">All Notifications</span></NavLink></li>
                        </ul>}
                    </li>


                </ul>
            </div>
        </div>
        <div className="sidebar-account">
          {accountOpen && <div className="sidebar-account-menu">
            <button type="button" onClick={() => { setAccountOpen(false); navigate("/change-password"); }}><i className="bi bi-key"></i><span>Change password</span></button>
            <button type="button" className="sidebar-account-logout" onClick={handleLogout}><i className="bi bi-box-arrow-right"></i><span>Log out</span></button>
          </div>}
          <button type="button" className="sidebar-account-trigger" onClick={() => setAccountOpen((open) => !open)} aria-expanded={accountOpen}>
            <span className="sidebar-account-avatar">{initials}</span>
            <span className="sidebar-account-copy"><strong>{adminName}</strong><small>Administrator</small></span>
            <i className={`bi bi-chevron-${accountOpen ? "down" : "up"} sidebar-account-chevron`}></i>
          </button>
        </div>
   </div>
    </div>
  )
}

export default Sidebar
